import type React from "react";
import { jsPDF } from "jspdf";
import QRCode from "qrcode";
import { useSelector } from "react-redux";
import { formatDateTimeWithMonth } from "../../../../utils/dateTimeUtils";

interface ThermalBillReportProps {
  open: boolean;
  onClose: () => void;
  data?: any;
}

// 80mm roll
const PAPER_WIDTH = 80;
const MARGIN_LEFT = 4;
const MARGIN_RIGHT = 76;
const CONTENT_WIDTH = MARGIN_RIGHT - MARGIN_LEFT;

const calculateAge = (dob: string, referenceDate?: string): string => {
  if (!dob) return "--";
  const birthDate = new Date(dob);
  const today = referenceDate ? new Date(referenceDate) : new Date();

  let years = today.getFullYear() - birthDate.getFullYear();
  let months = today.getMonth() - birthDate.getMonth();
  let days = today.getDate() - birthDate.getDate();

  if (days < 0) {
    months--;
    const lastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
    days += lastMonth.getDate();
  }
  if (months < 0) {
    years--;
    months += 12;
  }

  years = Math.max(0, years);
  months = Math.max(0, months);
  days = Math.max(0, days);

  return `${years}y-${months}m-${days}d`;
};

const ThermalBillReport: React.FC<ThermalBillReportProps> = ({
  open,
  onClose,
  data,
}) => {
  const profile = useSelector((state: any) => state.profileSlice.profile);
  const money = (value: any) => Number(value ?? 0).toFixed(2);

  const centerText = (doc: jsPDF, text: string, y: number) => {
    doc.text(text, PAPER_WIDTH / 2, y, { align: "center" });
  };

  const rightText = (doc: jsPDF, text: string, y: number) => {
    doc.text(text, MARGIN_RIGHT, y, { align: "right" });
  };

  const separator = (doc: jsPDF, y: number, dashed = false) => {
    doc.setLineWidth(0.2);
    if (dashed) doc.setLineDashPattern([0.5, 0.5], 0);
    doc.line(MARGIN_LEFT, y, MARGIN_RIGHT, y);
    if (dashed) doc.setLineDashPattern([], 0);
  };

  // label on the left, value (bold) right after it, wrapped when too long
  const infoRow = (doc: jsPDF, label: string, value: any, y: number) => {
    const labelX = MARGIN_LEFT;
    const valueX = MARGIN_LEFT + 20;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);
    doc.text(label, labelX, y);
    doc.setFont("helvetica", "bold");
    const lines: string[] = doc.splitTextToSize(
      String(value ?? "--"),
      MARGIN_RIGHT - valueX
    );
    lines.forEach((line, i) => {
      doc.text(line, valueX, y + i * 3.4);
    });
    doc.setFont("helvetica", "normal");
    return y + lines.length * 3.4;
  };

  const totalRow = (
    doc: jsPDF,
    label: string,
    value: string,
    y: number,
    bold = false
  ) => {
    doc.setFont("helvetica", bold ? "bold" : "normal");
    doc.setFontSize(bold ? 8.5 : 8);
    doc.text(label, MARGIN_LEFT, y);
    rightText(doc, value, y);
    doc.setFont("helvetica", "normal");
    return y + 4;
  };

  // Draws the whole receipt and returns the final Y position.
  // Called twice: once on a scratch page to measure the height, once for real.
  const drawReceipt = (doc: jsPDF, qrDataUrl: string | null): number => {
    let y = 6;

    // --- Hospital header ---
    if (data?.pageHeader?.title) {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      const titleLines: string[] = doc.splitTextToSize(
        String(data.pageHeader.title),
        CONTENT_WIDTH
      );
      titleLines.forEach((line) => {
        centerText(doc, line, y);
        y += 4;
      });
    }

    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    if (data?.pageHeader?.address) {
      const addressLines: string[] = doc.splitTextToSize(
        String(data.pageHeader.address),
        CONTENT_WIDTH
      );
      addressLines.forEach((line) => {
        centerText(doc, line, y);
        y += 3.2;
      });
    }

    const phones = [data?.pageHeader?.phone, data?.pageHeader?.phone2]
      .filter(Boolean)
      .join(", ");
    if (phones) {
      centerText(doc, phones, y);
      y += 3.2;
    }
    if (data?.pageHeader?.PHC_REG_NO) {
      centerText(doc, `PHC Reg No: ${data.pageHeader.PHC_REG_NO}`, y);
      y += 3.2;
    }

    y += 1;
    separator(doc, y);
    y += 4;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    centerText(doc, "PATIENT BILL", y);
    y += 4;
    separator(doc, y, true);
    y += 4;

    // --- Patient / sample info ---
    y = infoRow(doc, "MR No:", data?.patient?.mrn || "--", y);
    y = infoRow(doc, "Ref MRN:", data?.patient?.ref_mrn || "--", y);
    y = infoRow(doc, "TR No:", data?.sampleCollection?.trNo || "--", y);
    y = infoRow(doc, "Patient:", data?.patient?.patientName || "--", y);
    y = infoRow(doc, "Guardian:", data?.patient?.guardiansName || "--", y);
    y = infoRow(
      doc,
      "Age/Sex:",
      `${calculateAge(
        data?.patient?.dob,
        data?.sampleCollection?.createdAt
      )} / ${data?.patient?.gender || "--"}`,
      y
    );
    y = infoRow(doc, "Phone:", data?.patient?.phonNumber || "--", y);
    y = infoRow(
      doc,
      "Coll. Date:",
      data?.sampleCollection?.collectionDate
        ? formatDateTimeWithMonth(data.sampleCollection.collectionDate)
        : "--",
      y
    );
    y = infoRow(
      doc,
      "Referred By:",
      data?.sampleCollection?.otherDetails?.referredBy || "--",
      y
    );
    y = infoRow(
      doc,
      "Reg. At:",
      data?.collectionCenter?.collectionCenterName || "--",
      y
    );

    y += 1;
    separator(doc, y);
    y += 3.5;

    // --- Tests table ---
    const SR_X = MARGIN_LEFT;
    const NAME_X = MARGIN_LEFT + 6;
    const NAME_WIDTH = MARGIN_RIGHT - NAME_X - 14;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.text("#", SR_X, y);
    doc.text("Test", NAME_X, y);
    rightText(doc, "Price", y);
    y += 1.5;
    separator(doc, y);
    y += 3.8;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);
    (data?.rows || []).forEach((row: any, index: number) => {
      const nameLines: string[] = doc.splitTextToSize(
        String(row?.testName ?? "--"),
        NAME_WIDTH
      );
      nameLines.forEach((line, i) => {
        if (i === 0) {
          doc.text(String(index + 1), SR_X, y);
          rightText(doc, money(row?.price), y);
        }
        doc.text(line, NAME_X, y);
        y += 3.4;
      });
    });

    separator(doc, y);
    y += 4;

    // --- Totals ---
    y = totalRow(doc, "Gross Amount:", money(data?.paymentDetail?.grossAmount), y);
    y = totalRow(doc, "Discount:", money(data?.paymentDetail?.discountInAmount), y);
    y = totalRow(doc, "Net Amount:", money(data?.paymentDetail?.netAmount), y, true);
    y = totalRow(doc, "Paid Amount:", money(data?.paymentDetail?.nowPaying), y);
    y = totalRow(doc, "Due Amount:", money(data?.paymentDetail?.dueAmount), y, true);

    separator(doc, y);
    y += 4;

    // --- QR code ---
    if (qrDataUrl) {
      const qrSize = 24;
      doc.addImage(
        qrDataUrl,
        "PNG",
        (PAPER_WIDTH - qrSize) / 2,
        y,
        qrSize,
        qrSize
      );
      y += qrSize + 3;
      doc.setFont("helvetica", "bold");
      doc.setFontSize(7);
      centerText(doc, "Scan QR Code to download Report", y);
      y += 4;
    }

    const labUsername = String(data?.sampleCollection?.auth?.username ?? "").trim();
    const labPassword = String(data?.sampleCollection?.auth?.password ?? "").trim();
    if (labUsername || labPassword) {
      y += 1;
      if (labUsername) y = infoRow(doc, "Username:", labUsername, y);
      if (labPassword) y = infoRow(doc, "Password:", labPassword, y);
      y += 1;
    }

    // --- Footer ---
    doc.setFont("helvetica", "normal");
    doc.setFontSize(6.5);

    const invoiceFooter = data?.pageFooter?.invoiceFooter;
    if (invoiceFooter) {
      const mobileText = [invoiceFooter.mob1, invoiceFooter.mob2]
        .filter(Boolean)
        .join(", ");
      if (mobileText) {
        centerText(doc, mobileText, y);
        y += 3;
      }
      if (invoiceFooter.address) {
        const addressLines: string[] = doc.splitTextToSize(
          String(invoiceFooter.address),
          CONTENT_WIDTH
        );
        addressLines.forEach((line) => {
          centerText(doc, line, y);
          y += 3;
        });
      }
    }

    // Printed By / Registered By (same sources as A4 invoice)
    y += 2;
    separator(doc, y, true);
    y += 3.5;

    const printedByName =
      profile?.data?.fullName ||
      profile?.data?.firstName ||
      data?.pageHeader?.title ||
      "N/A";
    const registeredByName =
      data?.sampleCollection?.createdBy?.fullName ||
      data?.sampleCollection?.createdBy?.firstName ||
      "N/A";

    doc.setFont("helvetica", "bold");
    doc.setFontSize(7);
    const printedByLines: string[] = doc.splitTextToSize(
      `Printed By: ${printedByName}`,
      CONTENT_WIDTH
    );
    printedByLines.forEach((line) => {
      doc.text(line, MARGIN_LEFT, y);
      y += 3.2;
    });

    const registeredByLines: string[] = doc.splitTextToSize(
      `Registered By: ${registeredByName}`,
      CONTENT_WIDTH
    );
    registeredByLines.forEach((line) => {
      doc.text(line, MARGIN_LEFT, y);
      y += 3.2;
    });

    return y;
  };

  const generatePDF = async () => {
    // QR with the same download link the A4 invoice uses
    let qrDataUrl: string | null = null;
    try {
      const trNumber = data?.sampleCollection?.trNo || "";
      const projectId = data?.pageHeader?.projectId || "";
      qrDataUrl = await QRCode.toDataURL(
        `${process.env.REACT_APP_QR_BASE_URL}?tr=${trNumber}_${projectId}`
      );
    } catch (error) {
      console.error("Error generating QR code:", error);
    }

    // First pass on a scratch page just to measure how tall the receipt is,
    // so the roll is cut right after the last line instead of at A4 height.
    const measureDoc = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: [PAPER_WIDTH, 1000],
    });
    const contentHeight = drawReceipt(measureDoc, qrDataUrl);

    const doc = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: [PAPER_WIDTH, Math.ceil(contentHeight + 6)],
    });
    drawReceipt(doc, qrDataUrl);

    // Print handling (same hidden-iframe flow as the A4 invoice)
    doc.autoPrint();
    const blob = doc.output("blob");
    const blobUrl = URL.createObjectURL(blob);

    const iframe = document.createElement("iframe");
    iframe.style.position = "fixed";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "none";
    document.body.appendChild(iframe);

    const cleanup = () => {
      if (document.body.contains(iframe)) {
        document.body.removeChild(iframe);
        URL.revokeObjectURL(blobUrl);
      }
      window.removeEventListener("focus", cleanup);
    };

    window.addEventListener("focus", cleanup);

    iframe.src = blobUrl;
    iframe.onload = () => {
      try {
        const win = iframe.contentWindow;
        if (win) {
          win.focus();
          win.print();
        }
      } catch (e) {
        console.error("Error triggering print dialog:", e);
        cleanup();
      }
    };

    setTimeout(() => {
      try {
        const win = iframe.contentWindow;
        if (win && document.body.contains(iframe)) {
          win.focus();
          win.print();
        }
      } catch (e) {
        console.error("Fallback print attempt failed:", e);
        cleanup();
      }
    }, 1000);
  };

  if (open) {
    generatePDF();
    onClose();
  }

  return null;
};

export default ThermalBillReport;
